import React from "react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/FinalCTA";
import { TrendingUp, BarChart3, Sparkles, Calendar } from "lucide-react";
import { useT } from "@/hooks/useT";

export default function RevenueManagement() {
  const t = useT();
  const CARDS = [
    [TrendingUp, t("Sugjerime çmimi","Rate suggestions"),                     t("Bazuar në kërkesën reale","Based on real demand")],
    [Calendar,   t("Rregulla sezonale","Seasonal rules"),                     t("Çmime për verën, dimrin dhe festat","Summer, winter and holiday rates")],
    [BarChart3,  t("Parashikim 90 ditë","90-day forecast"),                    t("Për të planifikuar përpara","For planning ahead")],
    [Sparkles,   t("Min-stay dhe close-to-arrival","Min-stay & close-to-arrival"), t("Sipas datës ose dhomës","By date or by room")],
  ];
  return (
    <>
      <SEO
        title={t("Menaxhimi i të Ardhurave — Revenue Management","Revenue Management — Dynamic Pricing")}
        description={t(
          "Revenue Management nga Akomoda: çmime dinamike, smart pricing, parashikim okupance dhe rritje e të ardhurave për hotele në Shqipëri dhe Ballkan.",
          "Akomoda's Revenue Management: dynamic pricing, smart pricing, occupancy forecast and revenue growth for hotels in Albania and the Balkans."
        )}
      />
      <PageHeader
        eyebrow="Revenue Management"
        title={t("Menaxhim i të Ardhurave me çmime dinamike","Revenue Management with dynamic pricing")}
        description={t(
          "Rritni okupancën dhe maksimizoni të ardhurat me çmime që përshtaten automatikisht sipas kërkesës, sezonit dhe konkurrencës.",
          "Lift occupancy and maximise revenue with rates that adjust automatically to demand, seasonality and the competition."
        )}
        ctas={[{ to: "/demo", label: t("Kërko Demo","Request Demo") }]}
        breadcrumb={[
          { label: t("Platforma","Platform"), to: "/platforma" },
          { label: t("Menaxhimi i të Ardhurave","Revenue Management") },
        ]}
      />
      <section className="bg-surface-1">
        <div className="container-akm grid gap-10 py-14 md:grid-cols-12 md:py-20">
          <div className="md:col-span-6">
            <h2 className="h2-akm">{t("Çmime që punojnë për ju, edhe kur ju nuk jeni në recepsion","Rates that work for you, even when you're not at the desk")}</h2>
            <p className="lead mt-3">
              {t(
                "Akomoda sheh okupancën, sezonin dhe ngjarjet lokale, dhe ju jep sugjerime për çmimin — ose i përshtat vetë sipas rregullave që ju vendosni.",
                "Akomoda watches occupancy, seasonality and local events, then suggests the right rate — or adjusts it automatically using the rules you set."
              )}
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {CARDS.map(([Icon, tt, d]) => (
                <div key={tt} className="card-akm">
                  <Icon size={20} className="text-[#FF7A00]" />
                  <div className="mt-2 font-display text-base font-bold text-ink">{tt}</div>
                  <div className="text-sm text-slate2">{d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="rounded-xl border border-line bg-white p-6 shadow-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate2">{t("Parashikim 14 ditë","14-day forecast")}</div>
              <div className="mt-2 grid grid-cols-7 gap-1.5 text-center text-[11px]">
                {[60, 72, 84, 92, 88, 95, 78, 65, 70, 82, 90, 96, 99, 94].map((v, i) => (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <div className="h-20 w-full rounded-md bg-bg p-0.5">
                      <div className="h-full w-full rounded-sm bg-[#FF7A00]/15">
                        <div className="w-full rounded-sm bg-[#FF7A00]" style={{ height: `${v}%` }} />
                      </div>
                    </div>
                    <span className="text-slate2">{18 + i}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-md border border-line bg-bg p-3 text-sm">
                <div className="font-display text-base font-bold text-ink">{t("Sugjerim për nesër","Tomorrow's suggestion")}</div>
                <p className="mt-1 text-slate2">
                  {t(
                    <>Okupanca pritet të jetë 92%. Provo të rrisësh çmimin e dhomës Standard me <strong className="text-ink">+€8</strong>.</>,
                    <>Occupancy is expected to hit 92%. Try lifting the Standard room rate by <strong className="text-ink">+€8</strong>.</>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
